/**
 * The signed-in member's included monthly model allowance and on-demand
 * overage in the active organization: pounds used this calendar month,
 * what is left, what any excess is billed at, and whether an admin has
 * switched on-demand usage off for them.
 *
 * A per-org cached atom over the workspace server's
 * `/api/amoreg/model-spend` (same idiom as `state/orgProfile.ts`). The
 * server decides the figure, the person and the tenancy; the page only
 * renders it. A server without a ledger, or a session without a platform
 * user, answers 404 and the page shows nothing.
 *
 * @module state/modelSpend
 */
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useCallback, useMemo } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";
import { appAtomRegistry } from "../rpc/atomRegistry";

export const ModelSpendState = Schema.Literals(["included", "overage", "blocked", "unlimited", "unknown"]);
export type ModelSpendState = typeof ModelSpendState.Type;

const ModelSpendSchema = Schema.Struct({
  organizationId: Schema.String,
  userId: Schema.String,
  /** `YYYY-MM`, the UTC month the figure covers. */
  month: Schema.String,
  /** `YYYY-MM-DD`, when the allowance resets. */
  resetsOn: Schema.String,
  state: ModelSpendState,
  spentGbp: Schema.Number,
  includedGbp: Schema.NullOr(Schema.Number),
  remainingGbp: Schema.NullOr(Schema.Number),
  overageGbp: Schema.Number,
  overageMultiplier: Schema.Number,
  overageChargeGbp: Schema.Number,
  onDemandEnabled: Schema.Boolean,
  paidLimitGbp: Schema.optional(Schema.Number),
  /** What the person is told when their turns are refused; null otherwise. */
  message: Schema.NullOr(Schema.String),
});
export type ModelSpendStatus = typeof ModelSpendSchema.Type;

export const parseModelSpendStatus = Schema.decodeUnknownEffect(ModelSpendSchema);

const spendUrl = (organizationId: string) =>
  resolvePrimaryEnvironmentHttpUrl("/api/amoreg/model-spend", { organizationId });

const fetchSpend = (organizationId: string) =>
  Effect.gen(function* () {
    if (organizationId.length === 0) return null;
    const response = yield* Effect.tryPromise(() =>
      fetch(spendUrl(organizationId), { credentials: "include" }),
    );
    if (!response.ok) return null;
    const json = yield* Effect.tryPromise(() => response.json());
    return yield* parseModelSpendStatus(json);
  }).pipe(Effect.orElseSucceed(() => null));

const spendRuntime = Atom.runtime(Layer.empty);
const spendAtom = Atom.family((organizationId: string) =>
  spendRuntime
    .atom(fetchSpend(organizationId))
    .pipe(Atom.withLabel(`web-amoreg:model-spend:${organizationId}`)),
);

export interface ModelSpendView {
  /** Null while loading, when the server has no ledger, or on any failure. */
  readonly status: ModelSpendStatus | null;
  /** The read finished, so a null `status` means no ledger or a failure, not loading. */
  readonly loaded: boolean;
  readonly refresh: () => void;
}

export function useModelSpend(organizationId: string | null): ModelSpendView {
  const atom = useMemo(() => spendAtom(organizationId ?? ""), [organizationId]);
  const result = useAtomValue(atom);
  const refresh = useCallback(() => appAtomRegistry.refresh(atom), [atom]);
  return useMemo(
    () => ({ status: Option.getOrNull(AsyncResult.value(result)) ?? null, loaded: AsyncResult.isSuccess(result), refresh }),
    [refresh, result],
  );
}

/**
 * The meter's geometry, as shares of its full width. The track is the
 * allowance; past it, the whole bar re-scales to the spend so the excess
 * segment has room and the allowance boundary lands where the figures say.
 */
export interface ModelSpendMeter {
  /** Share of the bar the allowance occupies (1 while within it). */
  readonly includedShare: number;
  /** Share of the allowance used, capped at 1. */
  readonly usedShare: number;
  /** Share of the bar the excess occupies (0 while within the allowance). */
  readonly overageShare: number;
}

export const modelSpendMeter = (status: ModelSpendStatus): ModelSpendMeter | null => {
  if (status.includedGbp === null || status.includedGbp < 0) return null;
  if (status.state !== "overage" && status.state !== "blocked") {
    return {
      includedShare: 1,
      // A zero allowance is a full track with nothing to fill.
      usedShare: status.includedGbp === 0 ? 0 : Math.min(1, Math.max(0, status.spentGbp / status.includedGbp)),
      overageShare: 0,
    };
  }
  // Past a zero allowance the whole bar is excess; otherwise the allowance
  // keeps its share of the spend.
  const includedShare = status.spentGbp > 0 ? status.includedGbp / status.spentGbp : 1;
  return { includedShare, usedShare: 1, overageShare: 1 - includedShare };
};

/** A figure the page draws: the server read it, and an allowance applies. */
export type ShowableModelSpend = ModelSpendStatus & {
  readonly state: "included" | "overage" | "blocked";
  readonly includedGbp: number;
};

/** Only a figure the server could actually read against an allowance deserves the reader's eye. */
export const modelSpendIsShowable = (status: ModelSpendStatus | null): status is ShowableModelSpend =>
  status !== null &&
  status.includedGbp !== null &&
  (status.state === "included" || status.state === "overage" || status.state === "blocked");


/** The API derives authority from the session and permits only this member's cap. */
export async function saveModelPaidLimit(organizationId: string, userId: string, paidLimitGbp: number): Promise<string | null> {
  try {
    const response = await fetch(resolvePrimaryEnvironmentHttpUrl("/api/amoreg/org-profile/member", {
      organizationId, userId, action: "model-paid-limit",
    }), {
      method: "POST", credentials: "include", headers: { "content-type": "application/json" },
      body: JSON.stringify({ paidLimitGbp }),
    });
    return response.ok ? null : "Your paid usage limit could not be saved. Please try again.";
  } catch {
    return "Your paid usage limit could not be saved. Please try again.";
  }
}
